import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/auth';
// 0.36.227 — Ship B: sync user timezone to the format module so
// every formatDateTime() call resolves to the right zone.
import { setCurrentUserTimezone } from '../utils/format';
import { setVisitOwner, clearVisitApi } from '../lib/visitApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 0.36.227 — Mirror user.timezone into the format module whenever
  // user changes. Logout (user → null) resets to UTC.
  useEffect(() => {
    setCurrentUserTimezone(user?.timezone || null);
  }, [user]);
  // 0.48.86 - API answers kept for the visit belong to one person.
  useEffect(() => {
    if (!loading) setVisitOwner(user ? user.id : null);
  }, [user, loading]);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const result = await authApi.login(email, password);
    // 2FA gate: backend returned a challenge instead of a session. Don't set
    // user yet — caller (Login page) will redirect to the verify screen.
    if (result && result.requires_2fa) {
      return result;
    }
    // 0.36.201 — Login-method policy block (e.g. id_card_only).
    // Backend confirmed the password but is refusing this method.
    // No session was issued; forward the result so the Login page
    // can render the redirect UI without flipping us to a signed-
    // in state.
    if (result && result.login_method_blocked) {
      return result;
    }
    setUser(result);
    return result;
  };

  const completeChallenge = async (challenge_token, code, trust_device) => {
    const me = await authApi.submit2faChallenge(challenge_token, code, trust_device);
    setUser(me);
    return me;
  };

  const signup = async (email, password, extra) => {
    const created = await authApi.signup(email, password, extra);
    // 0.48.26 - Waits for an admin's approval: no sign-in yet.
    if (created && created.pending_approval) return { pending_approval: true };
    const me = await authApi.login(email, password);
    setUser(me);
    return me;
  };

  const logout = async () => {
    await authApi.logout();
    clearVisitApi();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, signup, logout, refresh, completeChallenge }}>
      {children}
    </AuthContext.Provider>
  );
}

// 0.48.51 - The user, or null outside an AuthProvider (a preview
// rendered on its own), for components that only read it.
export function useOptionalUser() {
  const ctx = useContext(AuthContext);
  return ctx ? ctx.user : null;
}

// 0.48.86 - Whether the session is still being read (false outside a provider).
export function useAuthLoading() {
  const ctx = useContext(AuthContext);
  return ctx ? ctx.loading : false;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
